/**
 * Phase 1.7 — Inventory module index, v2.
 *
 * Group-by: category. (Spec called for storage_location, but the SPA's
 * shared InventoryItem type doesn't surface storage_location_id today —
 * deferring that until Phase 1.10's API/type alignment.)
 *
 * KPI strip: In Stock | Below Threshold per spec. Cards include a
 * stock-level bar similar to the dashboard's LowStockCard for visual
 * continuity.
 */

import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Package, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Card,
  EmptyState,
  FilterBar,
  KpiCard,
  SectionHeader,
  ViewToggle,
  type ViewMode,
} from '@/components/v2';
import { authFetch } from '@/lib/auth';
import { useUiPrefs } from '@/lib/layout-flag';
import { usePageHeader } from '@/components/v2/layout/Header';
import { AddEntityDialog } from '@/components/v2/dialogs/AddEntityDialog';
import type { InventoryItem } from '@shared/types';

const VIEW_KEY = 'inventory';

export function InventoryPageV2(): JSX.Element {
  const { prefs, patch } = useUiPrefs();
  const view: ViewMode = prefs.view_modes?.[VIEW_KEY] ?? 'grid';
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [catFilter, setCatFilter] = useState<string>('all');
  const [stockFilter, setStockFilter] = useState<'all' | 'low'>('all');
  const [addOpen, setAddOpen] = useState<boolean>(false);
  const [refreshTick, setRefreshTick] = useState<number>(0);

  usePageHeader(
    'Inventory',
    <Button
      size="sm"
      onClick={() => setAddOpen(true)}
      className="bg-burnt-orange text-white hover:bg-burnt-orange/90 dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90"
    >
      <Plus className="h-4 w-4" />
      Add item
    </Button>,
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    async function load(): Promise<void> {
      try {
        const data = await authFetch<{ items: InventoryItem[] }>('/api/inventory');
        if (!cancelled) setItems(data.items ?? []);
      } catch (err) {
        if (!cancelled) setError((err as Error).message ?? 'Failed to load inventory');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  const categories = useMemo<string[]>(() => {
    const set = new Set<string>();
    for (const i of items) if (i.category) set.add(i.category);
    return [...set].sort();
  }, [items]);

  const filtered = useMemo<InventoryItem[]>(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i) => {
      if (q && !i.name.toLowerCase().includes(q)) return false;
      if (catFilter !== 'all' && i.category !== catFilter) return false;
      if (stockFilter === 'low' && !(i.quantity < i.min_quantity)) return false;
      return true;
    });
  }, [items, search, catFilter, stockFilter]);

  const grouped = useMemo<Record<string, InventoryItem[]>>(() => {
    const out: Record<string, InventoryItem[]> = {};
    for (const i of filtered) {
      const key = i.category || 'Uncategorized';
      (out[key] ??= []).push(i);
    }
    return out;
  }, [filtered]);

  const totals = useMemo(() => {
    const inStock = items.filter((i) => i.quantity >= i.min_quantity).length;
    const low = items.length - inStock;
    return { inStock, low };
  }, [items]);

  const setView = (next: ViewMode): void => {
    void patch({ view_modes: { ...(prefs.view_modes ?? {}), [VIEW_KEY]: next } });
  };

  const hasActiveFilters = Boolean(search) || catFilter !== 'all' || stockFilter !== 'all';
  const clear = (): void => {
    setSearch('');
    setCatFilter('all');
    setStockFilter('all');
  };

  return (
    <div className="space-y-6">
      <AddEntityDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        title="Add Item"
        endpoint="/api/inventory"
        entityType="inventory_item"
        fields={[
          { name: 'name', label: 'Name', required: true, placeholder: 'Diesel Fuel' },
          { name: 'category', label: 'Category', half: true, placeholder: 'Fuel' },
          { name: 'unit', label: 'Unit', half: true, required: true, placeholder: 'gallons' },
          { name: 'current_quantity', label: 'Current quantity', type: 'number', half: true, defaultValue: '0' },
          { name: 'low_stock_threshold', label: 'Low-stock threshold', type: 'number', half: true },
          { name: 'notes', label: 'Notes', type: 'textarea' },
        ]}
        transform={(v) => ({
          name: v.name,
          category: v.category || null,
          unit: v.unit,
          current_quantity: v.current_quantity ? Number(v.current_quantity) : 0,
          low_stock_threshold: v.low_stock_threshold ? Number(v.low_stock_threshold) : null,
          notes: v.notes || null,
        })}
        onAdded={() => setRefreshTick((t) => t + 1)}
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <KpiCard
          label="Inventory"
          icon={Package}
          primary={{ label: 'In Stock', value: totals.inStock }}
          secondary={{
            label: 'Below Threshold',
            value: totals.low,
            tone: totals.low > 0 ? 'warning' : 'default',
          }}
        />
        <KpiCard
          label="Filter"
          primary={{ label: 'Showing', value: filtered.length }}
          secondary={{ label: 'Total', value: items.length }}
        />
        <KpiCard
          label="Categories"
          primary={{ label: 'Distinct', value: categories.length }}
        />
      </div>

      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: 'Search items' }}
        filters={[
          <FilterSelect
            key="cat"
            value={catFilter}
            onChange={setCatFilter}
            options={[
              { value: 'all', label: 'All categories' },
              ...categories.map((c) => ({ value: c, label: c })),
            ]}
          />,
          <FilterSelect
            key="stock"
            value={stockFilter}
            onChange={(v) => setStockFilter(v as 'all' | 'low')}
            options={[
              { value: 'all', label: 'Any stock level' },
              { value: 'low', label: 'Low stock only' },
            ]}
          />,
        ]}
        actions={<ViewToggle value={view} onChange={setView} />}
        onClear={clear}
        hasActiveFilters={hasActiveFilters}
      />

      {error ? (
        <div className="rounded-lg bg-clay/10 p-4 text-sm text-clay dark:bg-clay-dark/20 dark:text-clay-dark">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-40 rounded-xl" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Package}
          title={hasActiveFilters ? 'No matches' : 'No inventory tracked yet'}
          description={
            hasActiveFilters
              ? 'Try clearing filters.'
              : 'Add an item to start tracking quantities, low-stock alerts, and consumption.'
          }
          action={
            hasActiveFilters ? (
              <Button onClick={clear} variant="outline">Clear filters</Button>
            ) : (
              <Button onClick={() => setAddOpen(true)}>
                <Plus className="h-4 w-4" />
                Add item
              </Button>
            )
          }
        />
      ) : (
        Object.keys(grouped)
          .sort()
          .map((cat) => (
            <SectionHeader key={cat} title={cat} count={grouped[cat].length} collapsible>
              {view === 'grid' ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {grouped[cat].map((i) => <InventoryCard key={i.id} item={i} />)}
                </div>
              ) : (
                <div className="overflow-hidden rounded-xl border border-border bg-surface dark:bg-surface-dark dark:border-border">
                  <table className="w-full text-sm">
                    <thead className="border-b border-border bg-surface-raised text-left dark:bg-surface-raised-dark">
                      <tr>
                        <Th>Item</Th>
                        <Th>Quantity</Th>
                        <Th>Threshold</Th>
                        <Th>Status</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {grouped[cat].map((i) => {
                        const isLow = i.quantity < i.min_quantity;
                        return (
                          <tr key={i.id} className="border-b border-border last:border-0">
                            <Td className="font-medium">{i.name}</Td>
                            <Td className="font-mono text-xs">{i.quantity} {i.unit}</Td>
                            <Td className="font-mono text-xs">{i.min_quantity} {i.unit}</Td>
                            <Td>
                              <span
                                className={`inline-flex rounded-full px-2 py-0.5 font-mono text-2xs uppercase tracking-wider ${
                                  isLow
                                    ? 'bg-clay/10 text-clay dark:bg-clay-dark/20 dark:text-clay-dark'
                                    : 'bg-moss/10 text-moss dark:bg-moss-dark/20 dark:text-moss-dark'
                                }`}
                              >
                                {isLow ? 'Low' : 'OK'}
                              </span>
                            </Td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </SectionHeader>
          ))
      )}
    </div>
  );
}

function InventoryCard({ item }: { item: InventoryItem }): JSX.Element {
  const isLow = item.quantity < item.min_quantity;
  const max = Math.max(item.quantity, item.min_quantity * 2, 1);
  const pct = Math.min((item.quantity / max) * 100, 100);
  return (
    <Card
      title={item.name}
      subtitle={item.category || 'Uncategorized'}
      badge={
        isLow ? (
          <span className="inline-flex rounded-full bg-clay/10 px-2 py-0.5 font-mono text-2xs uppercase tracking-wider text-clay dark:bg-clay-dark/20 dark:text-clay-dark">
            <AlertTriangle className="-mt-0.5 mr-1 inline h-3 w-3" />
            Low
          </span>
        ) : (
          <span className="inline-flex rounded-full bg-moss/10 px-2 py-0.5 font-mono text-2xs uppercase tracking-wider text-moss dark:bg-moss-dark/20 dark:text-moss-dark">
            OK
          </span>
        )
      }
    >
      <div className="flex items-baseline justify-between">
        <span className="font-display text-2xl tabular-nums">
          {item.quantity}
        </span>
        <span className="text-xs text-muted-foreground">
          {item.unit} · min {item.min_quantity}
        </span>
      </div>
      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-surface-raised dark:bg-surface-raised-dark">
        <div
          className={`h-full ${isLow ? 'bg-clay dark:bg-clay-dark' : 'bg-moss dark:bg-moss-dark'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </Card>
  );
}

function FilterSelect({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}): JSX.Element {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 rounded-md border border-border bg-surface px-2 text-sm shadow-sm dark:bg-surface-dark dark:border-border"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function Th({ children }: { children: React.ReactNode }): JSX.Element {
  return <th className="px-4 py-2 font-mono text-2xs uppercase tracking-wider text-muted-foreground">{children}</th>;
}
function Td({ children, className }: { children: React.ReactNode; className?: string }): JSX.Element {
  return <td className={`px-4 py-3 ${className ?? ''}`}>{children}</td>;
}
